import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, Lock, Eye, EyeOff, User, ArrowLeft } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { toast } from '@/stores/toastStore';
import { Button, Input, Card, CardContent } from '@/components/ui';
import { trackEvent } from '@/lib/analytics';
import { getAcquisitionEventParams } from '@/lib/acquisition';
import type { UserRole } from '@kulum/shared';

const registerSchema = z.object({
  displayName: z
    .string()
    .min(2, 'ニックネームは2文字以上で入力してください')
    .max(20, 'ニックネームは20文字以内で入力してください'),
  email: z.string().email('有効なメールアドレスを入力してください'),
  password: z
    .string()
    .min(8, 'パスワードは8文字以上で入力してください')
    .regex(/[a-zA-Z]/, 'パスワードには英字を含めてください')
    .regex(/[0-9]/, 'パスワードには数字を含めてください'),
  passwordConfirm: z.string(),
  agreeTerms: z.boolean().refine((val) => val === true, {
    message: '利用規約に同意してください',
  }),
}).refine((data) => data.password === data.passwordConfirm, {
  message: 'パスワードが一致しません',
  path: ['passwordConfirm'],
});

type RegisterFormData = z.infer<typeof registerSchema>;

export function RegisterPage() {
  const { role } = useParams<{ role: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { signUp, isLoading } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const wwwUrl = (import.meta.env.VITE_WWW_URL?.trim() || 'https://kulum.jp').replace(/\/$/, '');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      agreeTerms: false,
    },
  });

  // Validate role
  const validRole = role === 'donor' || role === 'donee' ? role : null;
  const registerStartTracked = (location.state as { registerStartTracked?: boolean } | null)?.registerStartTracked === true;

  useEffect(() => {
    if (validRole !== 'donee' || registerStartTracked) return;
    trackEvent('donee_register_start', {
      source: 'register_page',
      ...getAcquisitionEventParams(),
    });
  }, [registerStartTracked, validRole]);

  if (!validRole) {
    return (
      <div className="text-center py-8">
        <p className="text-neutral-600 mb-4">無効なページです</p>
        <Link to="/register" className="text-copper hover:underline">
          登録画面に戻る
        </Link>
      </div>
    );
  }

  const onSubmit = async (data: RegisterFormData) => {
    try {
      await signUp(data.email, data.password, data.displayName, validRole as UserRole);
      const acquisitionParams = getAcquisitionEventParams();
      trackEvent('sign_up', {
        method: 'email',
        role: validRole,
        ...acquisitionParams,
      });
      if (validRole === 'donee') {
        trackEvent('donee_register_complete', {
          method: 'email',
          role: validRole,
          ...acquisitionParams,
        });
      }
      toast.success('登録が完了しました。メールをご確認ください。');
      navigate('/verify-email');
    } catch (error) {
      // Error is handled in authStore
    }
  };

  return (
    <div className="animate-fade-in">
      {/* Back button */}
      <Link
        to="/register"
        className="inline-flex items-center gap-2 text-neutral-600 hover:text-copper mb-6 transition"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm">戻る</span>
      </Link>

      {/* Header */}
      <div className="text-center mb-8">
        <div
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm mb-4 ${
            validRole === 'donor'
              ? 'bg-copper/10 text-copper'
              : 'bg-rose-gold/10 text-rose-gold'
          }`}
        >
          {validRole === 'donor' ? 'ドナー登録' : 'ドニー登録'}
        </div>
        <h2 className="text-2xl font-heading-luxury text-luxury-dark mb-2">
          アカウント作成
        </h2>
        <p className="text-neutral-600">
          {validRole === 'donor'
            ? '精子提供者として登録します'
            : '精子提供を受ける方として登録します'}
        </p>
      </div>

      {/* Form */}
      <Card>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <Input
              {...register('displayName')}
              type="text"
              label="ニックネーム"
              placeholder="ニックネームを入力"
              error={errors.displayName?.message}
              leftIcon={<User className="w-5 h-5" />}
              hint="他のユーザーに表示される名前です"
            />

            <Input
              {...register('email')}
              type="email"
              label="メールアドレス"
              placeholder="example@email.com"
              error={errors.email?.message}
              leftIcon={<Mail className="w-5 h-5" />}
              autoComplete="email"
            />

            <Input
              {...register('password')}
              type={showPassword ? 'text' : 'password'}
              label="パスワード"
              placeholder="8文字以上（英字・数字を含む）"
              error={errors.password?.message}
              leftIcon={<Lock className="w-5 h-5" />}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-neutral-400 hover:text-neutral-600"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              }
              autoComplete="new-password"
            />

            <Input
              {...register('passwordConfirm')}
              type={showPasswordConfirm ? 'text' : 'password'}
              label="パスワード（確認）"
              placeholder="パスワードを再入力"
              error={errors.passwordConfirm?.message}
              leftIcon={<Lock className="w-5 h-5" />}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                  className="text-neutral-400 hover:text-neutral-600"
                >
                  {showPasswordConfirm ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              }
              autoComplete="new-password"
            />

            {/* Terms checkbox */}
            <div className="space-y-2">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  {...register('agreeTerms')}
                  className="mt-1 w-4 h-4 text-copper border-neutral-300 rounded focus:ring-copper"
                />
                <span className="text-sm text-neutral-600">
                  <a
                    href={`${wwwUrl}/terms`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-copper hover:underline"
                  >
                    利用規約
                  </a>
                  および
                  <a
                    href={`${wwwUrl}/privacy`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-copper hover:underline"
                  >
                    プライバシーポリシー
                  </a>
                  に同意します
                </span>
              </label>
              {errors.agreeTerms && (
                <p className="text-sm text-red-500">{errors.agreeTerms.message}</p>
              )}
            </div>

            <Button type="submit" fullWidth isLoading={isLoading}>
              登録する
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Login link */}
      <p className="text-center mt-6 text-neutral-600">
        すでにアカウントをお持ちの方は{' '}
        <Link to="/login" className="text-copper font-medium hover:underline">
          ログイン
        </Link>
      </p>
    </div>
  );
}
